import { Gamepad2, Keyboard, Smartphone } from "lucide-react";
import { slugify } from "./usePracticeSession";
import { play as sfx, playHover } from "@/utils/sounds";

function getCategoryClass(item, activeCategory, newCount) {
  if (activeCategory === item) return "border-[#FFE800] text-[#FFE800]";
  if (item === "new" && newCount > 0)
    return "border-green-500/60 text-green-400 hover:text-green-300 hover:border-green-400";
  return "border-white/10 text-gray-400 hover:text-[#FFE800] hover:border-[#FFE800]/40";
}

function getCategoryLabel(item, newCount) {
  if (item === "all") return "All Categories";
  if (item === "new") return `New (${newCount})`;
  return item;
}

const StratagemSelector = ({
  search,
  setSearch,
  category,
  setCategory,
  categories,
  newCount,
  filtered,
  selectedId,
  onSelect,
  loading,
  error,
  gamepadConnected,
  isTouchDevice,
}) => (
  <div className="space-y-6">
    <div className="hud-card clip-corners p-5">
      <h2
        className="font-headings text-2xl uppercase tracking-widest text-white"
        data-testid="practice-select-title"
      >
        Select Stratagem
      </h2>
      <div className="mt-4 space-y-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          className="w-full bg-black/50 border border-white/10 text-[#FFE800] font-data text-sm py-2 px-3 focus:border-[#FFE800] outline-none"
          data-testid="practice-search-input"
        />
        <div
          className="flex flex-wrap gap-2"
          data-testid="practice-category-filter"
        >
          {categories.map((item) => (
            <button
              key={item}
              onClick={() => { sfx("tab"); setCategory(item); }}
              onMouseEnter={playHover}
              className={`border px-3 py-1 text-xs font-headings uppercase tracking-[0.2em] ${getCategoryClass(item, category, newCount)}`}
              data-testid={`practice-category-${slugify(item)}`}
            >
              {getCategoryLabel(item, newCount)}
            </button>
          ))}
        </div>
      </div>
      {loading && (
        <p
          className="mt-4 text-sm text-gray-400"
          data-testid="practice-loading"
        >
          Syncing stratagem list...
        </p>
      )}
      {error && (
        <p className="mt-4 text-sm text-red-400" data-testid="practice-error">
          {error}
        </p>
      )}
      <div
        className="mt-4 max-h-[420px] space-y-2 overflow-y-auto pr-2"
        data-testid="practice-stratagem-list"
      >
        {filtered.map((item) => (
          <button
            key={item.id}
            onClick={() => { sfx("click"); onSelect(item.id); }}
            onMouseEnter={playHover}
            className={`w-full text-left border px-3 py-2 font-headings uppercase tracking-wide transition-all ${
              selectedId === item.id
                ? "border-[#FFE800] text-[#FFE800] bg-black/70"
                : "border-white/10 text-gray-300 hover:text-[#FFE800] hover:border-[#FFE800]/40"
            }`}
            data-testid={`practice-stratagem-${item.id}`}
          >
            <div className="flex items-center justify-between">
              <p>{item.name}</p>
              {item.is_new && (
                <span
                  className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-green-500 text-black rounded"
                  data-testid={`practice-new-badge-${item.id}`}
                >
                  NEW
                </span>
              )}
            </div>
            <p
              className="text-xs text-gray-500"
              data-testid={`practice-stratagem-category-${item.id}`}
            >
              {item.category}
            </p>
          </button>
        ))}
      </div>
    </div>
    <div className="hud-card clip-corners p-5" data-testid="practice-input-methods">
      <h3 className="font-headings text-xl uppercase tracking-widest text-white">
        Input Controls
      </h3>
      <div className="mt-3 space-y-2 text-xs text-gray-400">
        {gamepadConnected ? (
          <p className="text-[#FFE800]" data-testid="gamepad-connected">
            <Gamepad2 className="inline h-4 w-4 mr-2" />
            Gamepad connected — use D-pad
          </p>
        ) : isTouchDevice ? (
          <p data-testid="touch-hint">
            <Smartphone className="inline h-4 w-4 mr-2" />
            Use on-screen D-pad below
          </p>
        ) : (
          <p data-testid="keyboard-hint">
            <Keyboard className="inline h-4 w-4 mr-2" />
            Use <span className="text-[#FFE800]">WASD</span> or{" "}
            <span className="text-[#FFE800]">Arrow Keys</span>
          </p>
        )}
      </div>
    </div>
  </div>
);

export default StratagemSelector;
